import { useEffect, useState } from 'react';
import { Box, TextField, MenuItem, Button } from '@mui/material';
import { RecycleBinQuery, EntityType } from '../../types/softDelete.types';
import { fetchOrganisations } from '../../services/organisations.service';

const ENTITY_OPTIONS: { value: EntityType | ''; label: string }[] = [
  { value: '', label: 'All types' },
  { value: 'contact', label: 'Contact' },
  { value: 'client', label: 'Client' },
  { value: 'meeting', label: 'Meeting' },
  { value: 'file_note', label: 'File Note' },
  { value: 'agreement', label: 'Agreement' },
  { value: 'fact_find', label: 'Fact Find' },
  { value: 'note', label: 'Note' },
  { value: 'file_upload', label: 'File Upload' },
  { value: 'signature_envelope', label: 'Signature Envelope' },
  { value: 'drive_file', label: 'Drive File' },
];

interface OrgOption {
  _id: string;
  name?: string;
}

interface Props {
  filters: RecycleBinQuery;
  onChange: (filters: RecycleBinQuery) => void;
  onApply: () => void;
  /** When false, only org/name/client filters are shown (Deleted folders tab). */
  showEntityDateFilters?: boolean;
}

export default function RecycleBinFilters({
  filters,
  onChange,
  onApply,
  showEntityDateFilters = true,
}: Props) {
  const [orgList, setOrgList] = useState<OrgOption[]>([]);

  useEffect(() => {
    fetchOrganisations()
      .then((data: any) => setOrgList(data?.organisations ?? []))
      .catch(() => {});
  }, []);

  const set = (patch: Partial<RecycleBinQuery>) => onChange({ ...filters, ...patch });

  return (
    <Box display="flex" gap={2} alignItems="flex-end" flexWrap="wrap" mb={2}>
      {/* Organisation dropdown — resolved names instead of raw IDs */}
      <TextField
        select
        label="Organisation"
        size="small"
        value={filters.orgId ?? ''}
        onChange={(e) => set({ orgId: e.target.value || undefined })}
        sx={{ minWidth: 180 }}
      >
        <MenuItem value="">All organisations</MenuItem>
        {orgList.map((o) => (
          <MenuItem key={o._id} value={o._id}>
            {o.name?.trim() || o._id}
          </MenuItem>
        ))}
      </TextField>

      {/* Name search — works for both records and folders */}
      <TextField
        label="Name"
        size="small"
        value={filters.name ?? ''}
        onChange={(e) => set({ name: e.target.value || undefined })}
        sx={{ minWidth: 160 }}
        placeholder="Search by name…"
      />

      {/* Client name search */}
      <TextField
        label="Client"
        size="small"
        value={filters.clientName ?? ''}
        onChange={(e) => set({ clientName: e.target.value || undefined })}
        sx={{ minWidth: 160 }}
        placeholder="Search by client…"
      />

      {/* Records-only filters */}
      {showEntityDateFilters && (
        <>
          <TextField
            select
            label="Entity Type"
            size="small"
            value={filters.entityType ?? ''}
            onChange={(e) =>
              set({ entityType: (e.target.value as EntityType) || undefined })
            }
            sx={{ minWidth: 160 }}
          >
            {ENTITY_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Deleted By"
            size="small"
            value={filters.deletedBy ?? ''}
            onChange={(e) => set({ deletedBy: e.target.value || undefined })}
            sx={{ minWidth: 160 }}
            placeholder="Name or email…"
          />

          <TextField
            label="From"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={filters.from ?? ''}
            onChange={(e) => set({ from: e.target.value || undefined })}
          />

          <TextField
            label="To"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={filters.to ?? ''}
            onChange={(e) => set({ to: e.target.value || undefined })}
          />
        </>
      )}

      <Button variant="contained" onClick={onApply}>
        Apply
      </Button>
    </Box>
  );
}
